import { formatVisitControlPeriod } from '../../../utils/periodicity';
import { formatIsoDate } from '../../../utils/date';

import { randomInt, type Rng } from './rng';

export function toIsoDate(date: Date): string {
  return formatIsoDate(date);
}

export function toIsoDateTime(date: Date): string {
  return date.toISOString();
}

export function addMonths(date: Date, months: number): Date {
  const result = new Date(date.getTime());
  result.setMonth(result.getMonth() + months);
  return result;
}

export function addDays(date: Date, days: number): Date {
  const result = new Date(date.getTime());
  result.setDate(result.getDate() + days);
  return result;
}

export function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function formatControlPeriodRange(visitDate: Date, durationMonths: number): string {
  const label = formatVisitControlPeriod(visitDate, durationMonths);
  if (label) {
    return label;
  }

  const iso = toIsoDate(visitDate);
  return `${iso} (${durationMonths} mesi)`;
}

function normalizeDate(date: Date): Date {
  const normalized = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  normalized.setHours(0, 0, 0, 0);
  return normalized;
}

type WeekdayPlanner = {
  schedule: (baseDate: Date, options?: { allowFuture?: boolean }) => Date;
};

const MAX_DAILY_VISITS = 3;

function isWorkingDay(date: Date): boolean {
  const day = date.getDay();
  return day >= 1 && day <= 5; // Monday-Friday
}

function clampToWorkingDay(
  date: Date,
  { allowFuture, preferForward }: { allowFuture: boolean; preferForward: boolean }
): Date {
  const candidate = normalizeDate(date);
  let step = preferForward ? 1 : -1;
  if (step === 0) {
    step = allowFuture ? 1 : -1;
  }
  while (!isWorkingDay(candidate)) {
    candidate.setDate(candidate.getDate() + step);
  }
  return normalizeDate(candidate);
}

export function createWeekdayPlanner(_rng: Rng): WeekdayPlanner {
  const usage = new Map<string, number>();
  const weekdayUsage = new Map<number, number>();

  const schedule = (baseDate: Date, options: { allowFuture?: boolean } = {}): Date => {
    const { allowFuture = false } = options;
    const now = normalizeDate(new Date());
    const target = normalizeDate(baseDate);
    const offsets: number[] = [];
    for (let delta = 0; delta <= 31; delta++) {
      if (delta === 0) {
        offsets.push(0);
      } else {
        offsets.push(delta, -delta);
      }
    }

    const viable: Array<{ date: Date; weekday: number; distance: number }> = [];

    for (const offset of offsets) {
      const tentative = new Date(target);
      tentative.setDate(tentative.getDate() + offset);
      if (!allowFuture && tentative.getTime() > now.getTime()) {
        continue;
      }

      const adjusted = clampToWorkingDay(tentative, {
        allowFuture,
        preferForward: offset >= 0,
      });

      if (!allowFuture && adjusted.getTime() > now.getTime()) {
        continue;
      }

      const iso = toIsoDate(adjusted);
      const count = usage.get(iso) ?? 0;
      if (count < MAX_DAILY_VISITS) {
        viable.push({
          date: adjusted,
          weekday: adjusted.getDay(),
          distance: Math.abs(offset),
        });
      }
    }

    if (viable.length) {
      viable.sort((a, b) => {
        const usageA = weekdayUsage.get(a.weekday) ?? 0;
        const usageB = weekdayUsage.get(b.weekday) ?? 0;
        if (usageA !== usageB) {
          return usageA - usageB;
        }
        return a.distance - b.distance;
      });

      const chosen = viable[0]!;
      const iso = toIsoDate(chosen.date);
      usage.set(iso, (usage.get(iso) ?? 0) + 1);
      weekdayUsage.set(chosen.weekday, (weekdayUsage.get(chosen.weekday) ?? 0) + 1);
      return chosen.date;
    }

    // Fallback: assign to latest available working day not exceeding today
    let fallback = normalizeDate(now);
    fallback = clampToWorkingDay(fallback, {
      allowFuture: false,
      preferForward: false,
    });
    const iso = toIsoDate(fallback);
    usage.set(iso, (usage.get(iso) ?? 0) + 1);
    weekdayUsage.set(fallback.getDay(), (weekdayUsage.get(fallback.getDay()) ?? 0) + 1);
    return fallback;
  };

  return { schedule };
}

export function scheduleActionDueDate({
  rng,
  visitDate,
  frequencyMonths,
  status,
}: {
  rng: Rng;
  visitDate: Date;
  frequencyMonths: number;
  status: 'open' | 'closed';
}): Date {
  const now = normalizeDate(new Date());

  if (status === 'closed') {
    const closedDate = addDays(now, -randomInt(rng, 21, 35));
    return clampToWorkingDay(closedDate, {
      allowFuture: false,
      preferForward: false,
    });
  }

  const cadence = Math.max(1, frequencyMonths);
  const baseline = addMonths(visitDate, cadence);
  const futureAnchor =
    baseline.getTime() <= now.getTime()
      ? addDays(now, randomInt(rng, 14, 30 + cadence * 10))
      : baseline;

  return clampToWorkingDay(futureAnchor, {
    allowFuture: true,
    preferForward: true,
  });
}
